import { PDFDocument, StandardFonts, TextAlignment, rgb } from "pdf-lib";
import {
  DEFAULT_COMPANY,
  buildCompanyPayerBlock,
  formatT4aAmount,
} from "./t4aPdfFill";

const ZERO = "0.00";

const F = {
  date: "form1[0].Page1[0].Date[0]",
  payersAccount: "form1[0].Page1[0].EmployerInfo[0].PayersAccount[0]",
  employerName: "form1[0].Page1[0].EmployerInfo[0].EmployerName[0]",
  line88: "form1[0].Page1[0].Totals[0].Line88[0].Line88[0]",
  line16: "form1[0].Page1[0].Totals[0].Line16[0].Line16[0]",
  line18: "form1[0].Page1[0].Totals[0].Line18[0].Line18[0]",
  line20: "form1[0].Page1[0].Totals[0].Line20[0].Line20[0]",
  line22: "form1[0].Page1[0].Totals[0].Line22[0].Line22[0]",
  line24: "form1[0].Page1[0].Totals[0].Line24[0].Line24[0]",
  line28: "form1[0].Page1[0].Totals[0].Line28[0].Line28[0]",
  line30: "form1[0].Page1[0].Totals[0].Line30[0].Line30[0]",
  line32: "form1[0].Page1[0].Totals[0].Line32[0].Line32[0]",
  line34: "form1[0].Page1[0].Totals[0].Line34[0].Line34[0]",
  line40: "form1[0].Page1[0].Totals[0].Line40[0].Line40[0]",
  line42: "form1[0].Page1[0].Totals[0].Line42[0].Line42[0]",
  line48: "form1[0].Page1[0].Totals[0].Line48[0].Line48[0]",
  line101: "form1[0].Page1[0].Totals[0].Line101[0].Line101[0]",
  line82: "form1[0].Page1[0].Totals[0].Line82[0].Line82[0]",
  difference: "form1[0].Page1[0].Totals[0].Difference[0].Line22[0]",
  line84: "form1[0].Page1[0].Totals[0].Line84[0].Line84[0]",
  line86: "form1[0].Page1[0].Totals[0].Line86[0].Line86[0]",
  amountEnclosed:
    "form1[0].Page1[0].Totals[0].AmountEnclosed[0].AmountEnclosed[0]",
  line71: "form1[0].Page1[0].Totals[0].Registration1[0].Line71[0]",
  line72: "form1[0].Page1[0].Totals[0].Registration2[0].Line72[0]",
  line73: "form1[0].Page1[0].Totals[0].Registration3[0].Line73[0]",
  line74: "form1[0].Page1[0].Totals[0].Line74SIN[0].Line74[0]",
  line75: "form1[0].Page1[0].Totals[0].Line75SIN[0].Line75[0]",
  certName: "form1[0].Page1[0].Certification[0].Line76[0].Line76[0]",
  /** CRA box 078 — telephone area code (not signature). */
  areaCode: "form1[0].Page1[0].Certification[0].Line78[0].Line78[0]",
  certPhone: "form1[0].Page1[0].Certification[0].Telephone[0].Telephone[0]",
  certExt: "form1[0].Page1[0].Certification[0].Extentsion[0].Extention[0]",
  certDate: "form1[0].Page1[0].Certification[0].Date[0]",
  certPosition: "form1[0].Page1[0].Certification[0].Position[0]",
};

/** Amount boxes marked 0.00 on the CRA sample. */
const ZERO_FIELDS = [
  F.line16,
  F.line18,
  F.line22,
  F.line24,
  F.line28,
  F.line30,
  F.line32,
  F.line34,
  F.line40,
  F.line42,
  F.line48,
];

/** User-editable blanks (not auto-zeroed). */
export const T4A_SUMMARY_FILLABLE_DEFAULTS = {
  remittances: "",
  difference: "",
  overpayment: "",
  balanceDue: "",
  amountEnclosed: "",
  line101: "",
  registration1: "",
  registration2: "",
  registration3: "",
  preparedBySin1: "",
  preparedBySin2: "",
  certName: "",
  areaCode: "",
  certPhone: "",
  certExt: "",
  certDate: "",
  certPosition: "",
};

function setText(form, name, value) {
  try {
    const f = form.getTextField(name);
    // CRA boxes 071–073 (and similar) enforce short maxLength; clear so fills succeed.
    try {
      if (typeof f.removeMaxLength === "function") f.removeMaxLength();
    } catch (_) {}
    f.setText(value == null ? "" : String(value));
    return f;
  } catch (_) {
    try {
      return form.getTextField(name);
    } catch (__) {
      return null;
    }
  }
}

function widgetRect(textField) {
  const widgets = textField?.acroField?.getWidgets?.() || [];
  if (!widgets.length) return null;
  return widgets[0].getRectangle();
}

function paintJobsOnPage(page, font, jobs) {
  for (const job of jobs) {
    const { rect, text, align = "left", size = 9, multiline = false } = job;
    if (!rect) continue;
    page.drawRectangle({
      x: rect.x + 0.4,
      y: rect.y + 0.4,
      width: Math.max(0, rect.width - 0.8),
      height: Math.max(0, rect.height - 0.8),
      color: rgb(1, 1, 1),
    });
    const value = String(text ?? "");
    if (!value) continue;

    if (multiline) {
      const lines = value
        .split(/\n/)
        .map((l) => l.trim())
        .filter(Boolean);
      let y = rect.y + rect.height - size - 2;
      for (const line of lines) {
        page.drawText(line, {
          x: rect.x + 3,
          y,
          size,
          font,
          maxWidth: Math.max(8, rect.width - 6),
        });
        y -= size + 1.5;
        if (y < rect.y) break;
      }
      continue;
    }

    const tw = font.widthOfTextAtSize(value, size);
    let x = rect.x + 3;
    if (align === "right") x = rect.x + rect.width - tw - 4;
    if (align === "center") x = rect.x + (rect.width - tw) / 2;
    const y = rect.y + Math.max(1.5, (rect.height - size) / 2);
    page.drawText(value, { x, y, size, font });
  }
}

function moneyOrBlank(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  const n = Number(String(s).replace(/,/g, ""));
  if (!Number.isFinite(n)) return s;
  return formatT4aAmount(n);
}

/**
 * Fill CRA T4A Summary (t4asum-fill-25e).
 * Auto: HST #, company block, Box 088 (slip count), Box 020 (total payments), zeros.
 * Manual: remittances / difference / certification / etc. from `fillable`.
 */
export async function fillT4aSummaryPdfBytes(templateBytes, payload) {
  const {
    year,
    companyBlock,
    payerAccount = "",
    slipCount = 0,
    totalPayment = 0,
    fillable = {},
  } = payload;

  const pdf = await PDFDocument.load(templateBytes, {
    ignoreEncryption: true,
    updateMetadata: false,
  });
  const form = pdf.getForm();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const page = pdf.getPages()[0];

  const payerBlock =
    String(companyBlock || "").trim() ||
    buildCompanyPayerBlock(DEFAULT_COMPANY);
  const account = String(payerAccount || "").trim();
  const box88 = String(Math.max(0, Math.round(Number(slipCount) || 0)));
  const box20 = formatT4aAmount(totalPayment);
  const f = { ...T4A_SUMMARY_FILLABLE_DEFAULTS, ...fillable };
  // CRA prints "20" beside this box — fill the 2-digit year (e.g. 26 → 2026).
  const yearNum = Number(year) || new Date().getFullYear();
  const year2 = String(yearNum).slice(-2);

  /** @type {{rect: any, text: string, align?: string, size?: number, multiline?: boolean}[]} */
  const overlayJobs = [];

  function queueOverlay(tf, text, opts) {
    if (!tf) return;
    const rect = widgetRect(tf);
    if (!rect) return;
    const value = text == null ? "" : String(text);
    if (!value) return;
    overlayJobs.push({ rect, text: value, ...opts });
  }

  function setAmount(name, value, { overlay = true } = {}) {
    const tf = setText(form, name, value);
    if (!tf) return;
    try {
      tf.setAlignment(TextAlignment.Right);
    } catch (_) {}
    if (overlay && value) queueOverlay(tf, value, { align: "right", size: 9 });
  }

  const dateTf = setText(form, F.date, year2);
  queueOverlay(dateTf, year2, { align: "center", size: 11 });

  const accountTf = setText(form, F.payersAccount, account);
  queueOverlay(accountTf, account, { size: 8 });

  const nameTf = setText(form, F.employerName, payerBlock);
  if (nameTf) {
    try {
      nameTf.enableMultiline();
    } catch (_) {}
    queueOverlay(nameTf, payerBlock, { multiline: true, size: 7.5 });
  }

  setAmount(F.line88, box88);
  setAmount(F.line20, box20);

  for (const name of ZERO_FIELDS) {
    setAmount(name, ZERO);
  }

  // Fillable blanks (included in PDF so Post saves a complete form).
  setAmount(F.line82, moneyOrBlank(f.remittances));
  setAmount(F.difference, moneyOrBlank(f.difference));
  setAmount(F.line84, moneyOrBlank(f.overpayment));
  setAmount(F.line86, moneyOrBlank(f.balanceDue));
  setAmount(F.amountEnclosed, moneyOrBlank(f.amountEnclosed));
  setAmount(F.line101, moneyOrBlank(f.line101));

  // Prefer overlay paint for 071–075 so values show even if AcroForm maxLength blocks setText.
  const reg1 = String(f.registration1 || "").trim();
  const reg2 = String(f.registration2 || "").trim();
  const reg3 = String(f.registration3 || "").trim();
  const sin1 = String(f.preparedBySin1 || "").replace(/\D/g, "").slice(0, 9);
  const sin2 = String(f.preparedBySin2 || "").replace(/\D/g, "").slice(0, 9);
  const area = String(f.areaCode || f.certSignature || "").replace(/\D/g, "").slice(0, 3);

  queueOverlay(setText(form, F.line71, reg1), reg1, { size: 8 });
  queueOverlay(setText(form, F.line72, reg2), reg2, { size: 8 });
  queueOverlay(setText(form, F.line73, reg3), reg3, { size: 8 });
  queueOverlay(setText(form, F.line74, sin1), sin1, { size: 8 });
  queueOverlay(setText(form, F.line75, sin2), sin2, { size: 8 });

  const cn = setText(form, F.certName, f.certName);
  queueOverlay(cn, f.certName, { size: 9 });
  queueOverlay(setText(form, F.areaCode, area), area, { size: 9 });
  const cp = setText(form, F.certPhone, f.certPhone);
  queueOverlay(cp, f.certPhone, { size: 9 });
  const ce = setText(form, F.certExt, f.certExt);
  queueOverlay(ce, f.certExt, { size: 9 });
  const cd = setText(form, F.certDate, f.certDate);
  queueOverlay(cd, f.certDate, { size: 9 });
  const cpos = setText(form, F.certPosition, f.certPosition);
  queueOverlay(cpos, f.certPosition, { size: 9 });

  try {
    form.updateFieldAppearances(font);
  } catch (_) {}

  try {
    form.flatten();
  } catch (_) {}

  paintJobsOnPage(page, font, overlayJobs);

  return pdf.save();
}

export async function loadT4aSummaryTemplateBytes() {
  const res = await fetch(
    `${process.env.PUBLIC_URL || ""}/t4asum-fill-25e.pdf`,
  );
  if (!res.ok) {
    throw new Error("Could not load T4A Summary PDF template.");
  }
  return new Uint8Array(await res.arrayBuffer());
}

export function uint8ToBase64(bytes) {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(i, i + chunk),
    );
  }
  return btoa(binary);
}

export function base64ToUint8(b64) {
  const binary = atob(String(b64 || ""));
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}
